<script setup>
import { ref, nextTick } from 'vue'
import Confetti from 'vue-confetti/src/confetti'

// Initialize confetti
const confetti = new Confetti({})

// Reactive references for state
const playerSolution = ref("")
const number1 = ref(0)
const number2 = ref(0)
const solutionInput = ref(null)

// Class binding to highlight an incorrect attempt
const productClass = ref("")

// Control flags for the game
const inProgress = ref(false)
const gameSetup = ref(false)

// Game parameters
const max = ref(10)
const chosenTable = ref(2) // New: user-chosen times table
let currentScore = ref(0)
const winningScore = 10

// Audio for feedback sounds (files in public/)
const playSound = (filename) => {
  const audio = new Audio(`/${filename}`)
  audio.play().catch(() => {}) // Ignore autoplay restrictions
}

// Randomly generate the second number within the max range, first number is chosenTable
const setupProduct = () => {
  number1.value = chosenTable.value
  number2.value = Math.floor(Math.random() * max.value)
}

// Reset the game to start a new round
const resetGame = () => {
  inProgress.value = true
  setupProduct()
  nextTick(() => solutionInput.value?.focus())
}

// Initial game setup
const setupGame = () => {
  gameSetup.value = true
  resetGame()
}

// Handle submission of the user’s solution
const submitSolution = () => {
  productClass.value = ""

  // Check if user's input matches the product
  if (playerSolution.value == number1.value * number2.value) {
    playSound("correct.mp3")
    currentScore.value++
    playerSolution.value = ""
    setupProduct()
    solutionInput.value.focus()
  } else {
    playSound("incorrect.mp3")
    productClass.value = "incorrect"
  }

  // If player has reached the winning score, trigger confetti
  if (currentScore.value === winningScore) {
    inProgress.value = false
    playSound("fanfare.mp3")
    confetti.start()
    window.setTimeout(() => {
      confetti.stop()
      currentScore.value = 0
      resetGame()
      inProgress.value = true
    }, 7000)
  }
}
</script>

<template>
  <div class="game multiplication">
    <!-- If the game is set up, either show the game in progress or the "winner" message -->
    <div v-if="gameSetup">
      <div v-if="inProgress">
        <p>
          Enter the result of the multiplication below:
        </p>

        <div class="product">
          {{ number1 }} × {{ number2 }} = 
          <input
            class="solution"
            :class="productClass"
            ref="solutionInput"
            v-model="playerSolution"
            @keyup.enter.prevent="submitSolution"
            type="number"
          />
        </div>

        <div>
          <div class="button yes disable-touch-action" @click="submitSolution">
            Submit
          </div>
          <div>
            <progress id="file" :value="currentScore" :max="winningScore"></progress>
          </div>
        </div>
      </div>
      <div v-else class="winner">
        <p>Winner!</p>
      </div>
    </div>
    <!-- Before the game is set up, let the user choose the times table and the maximum multiplier -->
    <div class="setup" v-else>
      <p>Choose the times table and the maximum size of number to multiply</p>
      <div class="field">
        <label>Times Table</label>
        <input
          class="input"
          type="number"
          min="1"
          v-model="chosenTable"
          @focus="$event.target.select()"
        />
      </div>
      <div>
        <div class="button is-primary" @click="setupGame">
          Go
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.game {
  .product {
    font-size: 30px;
    margin: 30px;
  }

  input.solution {
    font-size: 30px;
    width: 80px;
    text-align: center;
    border: 1px solid rgba(120, 120, 120, 1);

    &:focus-visible,
    &:focus {
      outline: none;
    }

    &.incorrect {
      animation: pulse 1s 1;
    }
  }

  .button {
    border: 1px solid #ccc;
    display: inline-block;
    min-width: 100px;
    cursor: pointer;

    &.no {
      background-color: #ca0011;
      color: white;
    }

    &.yes {
      background-color: #12762b;
      color: white;
    }

    &.disable-touch-action {
      touch-action: manipulation;
    }
  }

  progress {
    margin: 20px;
  }

  @keyframes pulse {
    0% {
      border-color: rgba(120, 120, 120, 1);
    }
    70% {
      border-color: rgba(200, 30, 30, 1);
    }
    100% {
      border-color: rgba(120, 120, 120, 1);
    }
  }
}
</style>